export interface HelpTopic {
  title: string;
  description: string;
  url: string;
  related?: Array<{
    title: string;
    url: string;
  }>;
}

const normalizeHelpBaseUrl = (value: string) => value.replace(/\/+$/, "");

export const HELP_BASE_URL = normalizeHelpBaseUrl(
  process.env.REACT_APP_HELP_BASE_URL ||
    (process.env.NODE_ENV === "development" ? "http://localhost:3002/help" : "/help"),
);

export const HELP_HOME_URL = `${HELP_BASE_URL}/`;

const helpDocUrl = (path: string) => `${HELP_BASE_URL}/docs/${path.replace(/^\/+/, "")}`;

const systemReportPages = [
  { key: "invoice", title: "Invoice Report", slug: "invoice-report" },
  { key: "myInvoice", title: "My Invoice Report", slug: "my-invoice-report" },
  { key: "outstanding", title: "Outstanding Invoice Report", slug: "outstanding-invoice-report" },
  { key: "landman", title: "Landman Report", slug: "landman-report" },
  { key: "landmanPaymentSummary", title: "Landman Payment Summary", slug: "landman-payment-summary" },
  { key: "landmanMonthlyStatement", title: "Landman Monthly Statement", slug: "landman-monthly-statement" },
  { key: "landmanStatus", title: "Landman Status Report", slug: "landman-status-report" },
  { key: "timeEntryDetails", title: "Time Entry Details", slug: "time-entry-details" },
];

export const getSystemReportHelpUrl = (reportKey: string): string | null => {
  const page = systemReportPages.find(item => item.key === reportKey);
  return page ? helpDocUrl(`reports-and-invoicing/system-reports/${page.slug}`) : null;
};

export const helpTopics: Record<string, HelpTopic> = {
  "/dashboard": {
    title: "Understand your dashboard",
    description: "Review workspace activity, time trends, and operational status.",
    url: helpDocUrl("dashboard/understand-your-dashboard"),
    related: [
      { title: "Review operational status", url: helpDocUrl("dashboard/review-operational-status") },
    ],
  },
  "/signup": {
    title: "Create your company account",
    description: "Set up a new LandAgility company workspace.",
    url: helpDocUrl("getting-started/create-your-company-account"),
  },
  "/invite/accept": {
    title: "Accept an invitation",
    description: "Join a company after receiving a LandAgility invitation.",
    url: helpDocUrl("getting-started/accept-an-invitation"),
  },
  "/selecttenant": {
    title: "Sign in and choose a company",
    description: "Choose the company workspace you want to use.",
    url: helpDocUrl("getting-started/sign-in-and-choose-a-company"),
  },
  "/timeentry": {
    title: "Enter time",
    description: "Record daily work, expenses, and receipts.",
    url: helpDocUrl("time-and-expenses/enter-time"),
    related: [
      { title: "Add expenses", url: helpDocUrl("time-and-expenses/add-expenses") },
      { title: "Submit entries for approval", url: helpDocUrl("time-and-expenses/submit-entries-for-approval") },
    ],
  },
  "/approvals": {
    title: "Review submitted entries",
    description: "Approve or reject time and expense entries assigned to you.",
    url: helpDocUrl("approvals/review-submitted-entries"),
    related: [
      { title: "Approve or reject entries", url: helpDocUrl("approvals/approve-or-reject-entries") },
      { title: "Understand approval chains", url: helpDocUrl("approvals/understand-approval-chains") },
    ],
  },
  "/clients": {
    title: "Add and manage clients",
    description: "Maintain client details, billing rates, and setup.",
    url: helpDocUrl("clients-and-prospects/manage-clients"),
    related: [
      { title: "Assign billing roles", url: helpDocUrl("clients-and-prospects/assign-billing-roles") },
      { title: "Configure approvers", url: helpDocUrl("clients-and-prospects/configure-approvers") },
    ],
  },
  "/prospects": {
    title: "Add and manage prospects",
    description: "Track prospect setup, counties, assignments, and approvers.",
    url: helpDocUrl("clients-and-prospects/manage-prospects"),
    related: [
      { title: "Assign billing roles", url: helpDocUrl("clients-and-prospects/assign-billing-roles") },
      { title: "Configure approvers", url: helpDocUrl("clients-and-prospects/configure-approvers") },
    ],
  },
  "/reports": {
    title: "Understand system reports",
    description: "Understand report contents, filters, totals, and output options.",
    url: helpDocUrl("reports-and-invoicing/system-reports/"),
    related: [
      { title: "System reports overview", url: helpDocUrl("reports-and-invoicing/system-reports/overview") },
      ...systemReportPages.map(({ title, slug }) => ({
        title,
        url: helpDocUrl(`reports-and-invoicing/system-reports/${slug}`),
      })),
    ],
  },
  "/settings/company": {
    title: "Set up company information",
    description: "Keep company details current for reports and billing workflows.",
    url: helpDocUrl("company-setup/set-up-company-information"),
  },
  "/settings/users": {
    title: "Manage users and roles",
    description: "Invite users and control role-based access.",
    url: helpDocUrl("company-setup/manage-users-and-roles"),
  },
  "/settings/billing": {
    title: "View your subscription",
    description: "Review plan, billing, and subscription status.",
    url: helpDocUrl("billing/view-your-subscription"),
    related: [
      { title: "Change plans", url: helpDocUrl("billing/change-plans") },
      { title: "Understand feature availability", url: helpDocUrl("billing/understand-feature-availability") },
    ],
  },
  "/settings/features": {
    title: "Understand feature availability",
    description: "Learn why some pages and tools depend on plan features.",
    url: helpDocUrl("billing/understand-feature-availability"),
  },
  "/settings/jobtypes": {
    title: "Set up job types",
    description: "Manage the work categories users select on time entries.",
    url: helpDocUrl("company-setup/set-up-job-types"),
  },
  "/settings/expensetypes": {
    title: "Set up expense types",
    description: "Manage expense options available on time entries and reports.",
    url: helpDocUrl("company-setup/set-up-expense-types"),
  },
  "/settings/billingroles": {
    title: "Set up billing roles",
    description: "Manage billing role names, rates, and active status.",
    url: helpDocUrl("company-setup/set-up-billing-roles"),
  },
  "/settings/visibility": {
    title: "Configure visibility settings",
    description: "Control which users can see clients, prospects, and related work.",
    url: helpDocUrl("company-setup/configure-visibility-settings"),
  },
  "/settings/audit": {
    title: "Review audit history",
    description: "Review important changes in your company workspace.",
    url: helpDocUrl("company-setup/review-audit-history"),
  },
  "/editprofile": {
    title: "Update your profile",
    description: "Maintain your personal profile details.",
    url: helpDocUrl("profile/update-your-profile"),
  },
};

export const getHelpTopicForPath = (pathname: string): HelpTopic | null => {
  if (!pathname) return null;

  const exact = helpTopics[pathname];
  if (exact) return exact;

  const bestPrefix = Object.keys(helpTopics)
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  return bestPrefix ? helpTopics[bestPrefix] : null;
};
